import React from "react";
import { Form } from "react-bootstrap";
import C3 from "c3";
import {
  drawCharts,
  getChartTableInfo,
  ChartSize,
  createChartTableStyle,
  getSortProts,
} from "../common/chart";
import { ChartList, DataGridCheckRow } from "../../../../../common/constants";
import { IFileRow, IProtRow } from "../../../../../webapi/service/getFIles";
import "../../projectdetail.css";

function getSortRows(rows: IFileRow[], sortIds: number[]) {
  return sortIds
    .map((id) => {
      return rows.find((row) => {
        return row.id === id;
      });
    })
    .filter((row) => {
      return typeof row !== "undefined";
    }) as IFileRow[];
}

export interface IProps {
  /**
   * メインスペースの幅
   */
  mainSpaceWidth: number;
  /**
   * ファイル情報一覧
   */
  rows: IFileRow[];
  /**
   * チャート描画情報一覧
   */
  prots: IProtRow[];
  /**
   * チェック行一覧
   */
  checkRows: DataGridCheckRow[];
  /**
   * チャート描画順
   */
  sortIds: number[];
  /**
   * チェック状態が変更された場合に呼び出されます
   * @param selects - チェック行一覧
   */
  onChangeSelectRows: (selects: DataGridCheckRow[]) => void;
  /**
   * チャートが選択された場合に呼び出されます
   * @param rowid - 行ID
   */
  onSelecRow: (rowid: number) => void;
  /**
   * ローディング画面の表示非表示を切り替えます
   * @param loading - ローディング画面を表示する場合はtrue
   */
  onLoading: (loading: boolean) => void;
}

class ChartListPanel extends React.Component<IProps> {
  private readonly chartid_fmt = "chart_file_";
  private readonly checkid_fmt = "chart_check_";
  private readonly DefaultChartSize: ChartSize = {
    width: ChartList.FILES_WIDTH,
    height: ChartList.FILES_HEIGHT,
  };
  private _charts: C3.ChartAPI[] | null = null;
  private _chartSize: ChartSize = this.DefaultChartSize;

  constructor(props: IProps) {
    super(props);

    this.handleChangeChartCheck = this.handleChangeChartCheck.bind(this);
    this.chartSize = this.chartSize.bind(this);
  }

  chartSize(size?: ChartSize) {
    if (size) {
      this._chartSize = size;
    }
    return this._chartSize;
  }

  createCharts(): void {
    const { mainSpaceWidth, prots, sortIds } = this.props;
    const { chartWidth, chartHeight } = getChartTableInfo(
      mainSpaceWidth,
      this.DefaultChartSize,
      true
    );
    this._charts = drawCharts(
      getSortProts(prots, sortIds),
      this.chartid_fmt,
      chartWidth,
      chartHeight
    );
    this.chartSize({ width: chartWidth, height: chartHeight });
  }

  destroyCharts(): void {
    if (this._charts) {
      this._charts.forEach((chart) => {
        chart.destroy();
      });
      this._charts = null;
    }
  }

  componentDidMount(): void {
    const { onLoading } = this.props;
    setTimeout(() => {
      this.createCharts();
      onLoading(false);
    });
    onLoading(true);
  }

  componentDidUpdate(prevProps: IProps): void {
    const { onLoading, prots } = this.props;
    if (prots.length !== prevProps.prots.length) {
      setTimeout(() => {
        this.destroyCharts();
        this.createCharts();
        onLoading(false);
      });
      onLoading(true);
    }
  }

  componentWillUnmount(): void {
    this.destroyCharts();
  }

  handleChangeChartCheck(e: React.ChangeEvent<HTMLInputElement>): void {
    const { rows, checkRows, onChangeSelectRows } = this.props;
    const target = e.target;
    const row = rows.find((row) => {
      return this.checkid_fmt + String(row.id) === target.id;
    });
    if (row) {
      if (target.checked) {
        const selects = checkRows.slice();
        selects.push(row.id);
        onChangeSelectRows(selects);
      } else {
        const selects = checkRows.filter((checkRow) => checkRow !== row.id);
        onChangeSelectRows(selects);
      }
    }
  }

  createChartList(): JSX.Element[] {
    const { rows, checkRows, sortIds, onSelecRow } = this.props;
    const charts: JSX.Element[] = [];
    getSortRows(rows, sortIds).forEach((row, i) => {
      const id: number = row.id;
      const chartid: string = this.chartid_fmt + String(row.id);
      const checkid: string = this.checkid_fmt + String(row.id);
      const checked =
        typeof checkRows.find((row) => {
          return row === id;
        }) !== "undefined"
          ? true
          : false;
      charts.push(
        <div className="chart-list-item" key={i}>
          <div>
            <Form.Check
              id={checkid}
              label=" "
              checked={checked}
              onChange={this.handleChangeChartCheck}
            />
          </div>
          <div id={chartid} onClick={() => onSelecRow(row.id)}></div>
        </div>
      );
    });
    return charts;
  }

  render(): JSX.Element {
    const { mainSpaceWidth } = this.props;
    const chartTableStyle = createChartTableStyle(
      mainSpaceWidth,
      this.DefaultChartSize,
      this._charts,
      this.chartSize
    );
    const contents = this.createChartList();
    return (
      <div className="chart-list-area">
        <div style={chartTableStyle}>{contents}</div>
      </div>
    );
  }
}

/**
 * チャート一覧
 */
export default ChartListPanel;
